<template>
  <PopupDialog
    v-model:open="open"
    :title="$t('dialog.companion.title')"
    content-class="bg-base-100 max-w-7xl"
  >
    <div class="flex flex-col gap-6">
      <form
        class="card bg-base-200 shadow-sm"
        @submit.prevent="createSession(selectedTemplate.key, selectedTemplate.roles)"
      >
        <div class="card-body gap-4">
          <div class="card-title">
            {{ $t('dialog.companion.create-session') }}
          </div>
          <div class="flex items-end gap-2">
            <label class="form-control flex-1">
              <span class="label">
                <span class="label-text font-semibold">{{
                  $t('dialog.companion.template-label')
                }}</span>
              </span>
              <select v-model="selectedTemplateKey" class="select select-lg select-bordered w-full">
                <option
                  v-for="template in companionRoleTemplates"
                  :key="template.key"
                  :value="template.key"
                >
                  {{ $t(template.title) }}
                </option>
              </select>
            </label>
            <button
              type="button"
              class="btn btn-secondary btn-square btn-lg"
              :disabled="loading"
              @click="refetchCompanionSessions()"
            >
              <span v-if="loading" class="loading loading-spinner loading-sm" />
              <FontAwesomeIcon v-else :icon="faRefresh" />
            </button>
            <button type="submit" class="btn btn-primary btn-square btn-lg" :disabled="loading">
              <span v-if="loading" class="loading loading-spinner loading-sm" />
              <FontAwesomeIcon v-else :icon="faPlus" />
            </button>
          </div>
          <div class="card-actions">
            <p class="text-sm opacity-70">
              {{ $t(selectedTemplate.description) }}
            </p>
          </div>
        </div>
      </form>
      <div v-if="sessions?.length" class="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <CompanionSessionCard v-for="session in sessions" :key="session.id" :session="session">
          <template #actions>
            <button
              type="button"
              class="btn btn-error btn-sm"
              :disabled="loading"
              @click="removeSession(session.id)"
            >
              <FontAwesomeIcon :icon="faTrash" />
              {{ $t('companion.actions.end-session') }}
            </button>
          </template>
        </CompanionSessionCard>
      </div>
      <div v-else class="alert alert-info">
        {{ $t('dialog.companion.empty') }}
      </div>
    </div>
  </PopupDialog>
</template>

<script setup lang="ts">
import { faPlus, faRefresh, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { CompanionRole } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { computed, ref } from 'vue';
import { useCompanionRoleTemplateInfo } from '../../composables/companion/useCompanionRoleTemplateInfo';
import { useCompanionSessions } from '../../composables/companion/useCompanionSessions';
import { useLoading } from '../../stores/loadingStore.ts';
import CompanionSessionCard from './CompanionSessionCard.vue';
import PopupDialog from './PopupDialog.vue';

const open = defineModel('open', {
  type: Boolean,
  default: false,
});

const { loading } = useLoading();
const { sessions, createSession, removeSession, refetchCompanionSessions } = useCompanionSessions();
const { companionRoleTemplates } = useCompanionRoleTemplateInfo('text');

const selectedTemplateKey = ref<CompanionRole>(CompanionRole.FILE_SYNC);

const selectedTemplate = computed(
  () =>
    companionRoleTemplates.find((template) => template.key === selectedTemplateKey.value) ??
    companionRoleTemplates[0],
);
</script>
